import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkRemoveDuplicates } from '../../api/channelBulkAdvanced';
import { getApiErrorMessage } from '../../api/http';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getDuplicateRemovalStats } from '../../lib/duplicateRemoval';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

export default function RemoveDuplicatesModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();

  const [step, setStep] = useState<1 | 2>(1);
  const [keyField, setKeyField] = useState<'name' | 'url'>('name');
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [busy, setBusy] = useState(false);

  const selectedSet = useMemo(() => new Set(selectedIds.map(Number).filter((n) => Number.isFinite(n))), [selectedIds]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to fetch channels.'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setStep(1);
    setKeyField('name');
    if (selectedIds.length > 0) setScope('selected');
    else setScope('all');
    void load();
  }, [show, load, selectedIds.length]);

  const stats = useMemo(() => {
    return getDuplicateRemovalStats(channels, keyField, scope, selectedSet);
  }, [channels, keyField, scope, selectedSet]);

  async function apply() {
    if (!playlistId) {
      showToast('No playlist selected.', 'warning');
      return;
    }
    const pid = parseInt(playlistId, 10);
    if (!Number.isFinite(pid) || pid <= 0) {
      showToast('Invalid playlist id.', 'error');
      return;
    }
    if (stats.removedCount <= 0) {
      showToast('No duplicates found for the selected criteria.', 'warning');
      return;
    }
    setBusy(true);
    try {
      const payload = {
        playlist_id: pid,
        key: keyField,
        scope,
        channel_ids: scope === 'selected' ? [...selectedSet] : [],
      };
      const data = await bulkRemoveDuplicates(payload);
      if (!data.success) {
        showToast(data.message || 'Operation failed.', 'error');
        return;
      }
      showToast(data.message || `Removed ${data.deleted_count ?? 0} duplicates.`, 'success');

      const deletedIds = stats.deleteIds;
      if (deletedIds.length > 0) {
        record({
          label: 'Remove duplicates',
          undo: { type: 'setDeleted', playlist_id: pid, ids: deletedIds, deleted: false },
          redo: { type: 'setDeleted', playlist_id: pid, ids: deletedIds, deleted: true },
        });
      }
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server connection error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Remove duplicates"
      footer={
        step === 2 ? (
          <div className="d-flex justify-content-between w-100">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Previous
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" disabled={busy} onClick={() => void apply()}>
                {busy ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>
              Next
            </button>
          </div>
        )
      }
    >
      <div className="progress mb-3" style={{ height: 6 }}>
        <div className="progress-bar bg-primary" style={{ width: step === 1 ? '50%' : '100%' }} />
      </div>

      {step === 1 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 1</p>
          <label className="form-label">Delete all the streams having same:</label>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="radio"
              id="rdUrl"
              checked={keyField === 'url'}
              onChange={() => setKeyField('url')}
            />
            <label className="form-check-label" htmlFor="rdUrl">
              Url
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="radio"
              id="rdName"
              checked={keyField === 'name'}
              onChange={() => setKeyField('name')}
            />
            <label className="form-check-label" htmlFor="rdName">
              Name
            </label>
          </div>
        </>
      ) : (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 2</p>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="rdSel"
              checked={scope === 'selected'}
              disabled={selectedIds.length === 0 || !gridActive}
              onChange={() => setScope('selected')}
            />
            <label className="form-check-label" htmlFor="rdSel">
              {selectedIds.length} selected streams
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="radio"
              id="rdCat"
              checked={scope === 'categories'}
              onChange={() => setScope('categories')}
            />
            <label className="form-check-label" htmlFor="rdCat">
              Streams from categories
            </label>
          </div>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="radio"
              id="rdAll"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
            />
            <label className="form-check-label" htmlFor="rdAll">
              All streams
            </label>
          </div>
          <p className="small text-muted mt-3 mb-0">
            Preview: {stats.removedCount} duplicate(s) will be removed ({stats.checkedCount} streams considered).
          </p>
        </>
      )}
    </SimpleModal>
  );
}
